import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useUnit } from "effector-react";
import {
  Fragment,
  Suspense,
  lazy,
  useCallback,
  useMemo,
  useState,
} from "react";

import images from "../constants/images";
import { $gameConfig, $playerData, buildValleyBlock, setTask } from "../store";
import type {
  BuildObjects,
  ResourceObjects,
  TokenValleyBlockType,
} from "../types/tokenValley";
import { formatTimeDMS } from "../utils/formatTimeDMS";
import { formatTypesName } from "../utils/formatTypesName";
import { getResourceGatheringIconResourcesTypes } from "../utils/getResourceGatheringIcon";
import BlockTimer from "./BlockTimer";

const Dialog = lazy(() => import("@mui/material/Dialog"));

const ShipDialog = () => {
  const [open, setOpen] = useState(false);
  const [gameConfig, playerData] = useUnit([$gameConfig, $playerData]);
  const typeShip = "build/boat";

  const findShip = useCallback((): TokenValleyBlockType | undefined => {
    return playerData.valley_map?.["-1"];
  }, [playerData]);
  const ship = findShip();

  const configResources = useMemo(() => {
    return ship
      ? (gameConfig?.buildings_spec[typeShip]?.production_levels[1].consume ?? {})
      : (gameConfig?.buildings_spec[typeShip]?.production_levels[1].build_cost.materials ?? {});
  }, [gameConfig, ship]);

  const handleClick = useCallback(async () => {
    if (ship && ship.block_time_ready < Date.now()) {
      setTask({
        valley_block_id: -1,
        cycles_planned: 1
      });
    } else {
      await buildValleyBlock({
        position: -1,
        block_type: typeShip,
      });
    }
  }, [ship]);

  const canPurchase = (selectedBuild: BuildObjects | null): boolean => {
    if (!selectedBuild) return false;

    const materials =
      gameConfig?.buildings_spec?.[selectedBuild]?.production_levels[1].build_cost?.materials;

    if (!materials) return false;

    return Object.entries(materials).every(
      ([material, requiredAmount]) =>
        (playerData.player_resources?.[material] ?? 0) >= (requiredAmount ?? 0),
    );
  };

  return (
    <Fragment>
      <Box
        position={"absolute"}
        bottom={20}
        right={40}
        onClick={() => {
          setOpen(true);
        }}
      >
        <img
          src={ship ? images.sea.rebuildShip : images.sea.ruinedShip}
          alt="background"
          width={"106px"}
        />
        {ship && ship.block_time_ready > Date.now() && (
          <Box position={"relative"} bottom={15}>
            <BlockTimer block={ship} />
          </Box>
        )}
      </Box>

      <Suspense fallback={<CircularProgress size={40} />}>
        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          PaperProps={{
            sx: {
              minWidth: "90vw",
              border: "2px solid",
              borderRadius: "15px",
              bgcolor: "primary.bgModalBuild",
              borderColor: "secondary.borderInfoDialog",
            },
          }}
        >
          <Box
            height={"55px"}
            display={"flex"}
            alignItems={"center"}
            position={"relative"}
            bgcolor={"primary.main"}
            justifyContent={"center"}
            borderBottom={"2px solid"}
            borderColor={"primary.borderBlue"}
          >
            <Typography
              color="white"
              fontSize={"20px"}
              sx={{
                textShadow: `
                                         -2.2px -2.2px 1px #344871,
                                          2.2px -2.2px 1px #344871,
                                          -2.2px 2.2px 1px #344871,
                                          2.2px 2.2px 1px #344871
                                        `,
              }}
            >
              Expedition Boat
            </Typography>
            <Button
              onClick={() => setOpen(false)}
              variant="close"
              sx={{
                width: "37px",
                height: "37px",
                position: "absolute",
                right: "10px",
                borderRadius: "11px",
              }}
            >
              <img
                src={images.icons.XClose}
                alt=""
                style={{ width: "16px", height: "19px" }}
              />
            </Button>
          </Box>
          <Box display={"flex"} gap={1} p={2}>
            <Box
              width={"110px"}
              height={"110px"}
              display={"flex"}
              minWidth={"110px"}
              minHeight={"110px"}
              bgcolor={"#B9CFFF"}
              overflow={"hidden"}
              border={"2px solid"}
              borderRadius={"6px"}
              alignItems={"center"}
              borderColor={"#3C548C"}
              justifyContent={"center"}
              sx={{
                backgroundImage: `url(${images.icons.BgLine})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <img
                src={ship ? images.sea.rebuildShip : images.sea.ruinedShip}
                alt="ship"
                width={"100px"}
              />
            </Box>
            <Box
              p={1}
              overflow={"auto"}
              maxHeight={"110px"}
              borderRadius={"6px"}
              bgcolor={"secondary.bgDialogBuild"}
              sx={{
                "&::-webkit-scrollbar": { display: "none" },
              }}
              border={"2px solid"}
              borderColor={"secondary.bgDialogBuild"}
            >
              <Typography
                color="white"
                fontSize={"16px"}
                sx={{
                  textShadow: `
                                         -1.5px -1.5px 1.5px #344871,
                                          1.5px -1.5px 1.5px #344871,
                                          -1.5px 1.5px 1.5px #344871,
                                          1.5px 1.5px 1.5px #344871
                                        `,
                }}
              >
                Ship
              </Typography>
              <Typography
                fontWeight={900}
                fontSize={"10px"}
                fontFamily={"Red Hat Display"}
              >
                This ship will go on an expedition for a certain period of time.
                When it returns, it will bring back resources found during the
                journey. While the ship is away, it won’t be available for use.
                It’s a temporary investment for long-term gain.
              </Typography>
            </Box>
          </Box>
          <Box
            mb={2}
            mx={2}
            p={1}
            gap={1}
            display={"flex"}
            flexDirection={"column"}
            borderRadius={"6px"}
            bgcolor={"secondary.bgDialogBuild"}
          >
            <Box
              bgcolor={"#DDECFF"}
              borderRadius={"6px"}
              display={"flex"}
              gap={1}
              flexDirection={"column"}
              pb={1}
            >
              <Box
                width={"100%"}
                height={"21px"}
                display={"flex"}
                bgcolor={"#9FBCE1"}
                borderRadius={"6px 6px 0 0"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Typography fontSize={"12px"} color="primary.textResearchLab">
                  Resources required
                </Typography>
              </Box>

              {Object.keys(configResources ?? {}).map((item) => {
                return (
                  <Box key={item} display={"flex"} gap={1} ml={1}>
                    <Box
                      width={"45px"}
                      height={"45px"}
                      display={"flex"}
                      position={"relative"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      sx={{
                        backgroundImage: `url(${images.lab.bg})`,
                        backgroundSize: "cover",
                      }}
                    >
                      <img
                        style={{ width: "33px", height: "33px" }}
                        src={
                          getResourceGatheringIconResourcesTypes(
                            item as ResourceObjects,
                          ) || ""
                        }
                        alt={""}
                      />
                    </Box>
                    <Box display={"flex"} flexDirection={"column"}>
                      <Typography
                        fontSize={"10px"}
                        fontFamily={"Red Hat Display"}
                        color="primary.textResearchLab"
                      >
                        {formatTypesName(item as ResourceObjects)}
                      </Typography>
                      <Typography
                        color="white"
                        fontWeight={900}
                        fontSize={"16px"}
                        fontFamily={"Red Hat Display"}
                        sx={{
                          textShadow: `
                                               -1.3px -1.3px 1px #344871,
                                                1.3px -1.3px 1px #344871,
                                                -1.3px 1.3px 1px #344871,
                                                1.3px 1.3px 1px #344871
                                              `,
                        }}
                      >
                        {!ship && (
                          <span
                            style={{
                              color:
                                (playerData.player_resources?.[item] ?? 0) <
                                (configResources?.[item] ?? 0)
                                  ? "#E44754"
                                  : "white",
                            }}
                          >
                            {playerData.player_resources?.[item]?.toFixed(0) ??
                              0}{" "}
                            /{" "}
                          </span>
                        )}
                        {configResources?.[item] ?? 0}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box borderRadius={"6px"} bgcolor={"#DDECFF"} overflow={"hidden"}>
              <Box
                width={"100%"}
                height={"21px"}
                display={"flex"}
                bgcolor={"#9FBCE1"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Typography fontSize={"12px"} color="primary.textResearchLab">
                  Conversion
                </Typography>
              </Box>

              <Box display={"flex"} justifyContent={"space-between"} px={1}>
                <Typography
                  fontSize={"12px"}
                  color="primary.textResearchLab"
                  textAlign={"center"}
                  mt={1}
                >
                  Time
                </Typography>
                <Typography
                  fontSize={"12px"}
                  color="primary.textResearchLab"
                  textAlign={"center"}
                  mt={1}
                >
                  {formatTimeDMS(
                    gameConfig?.buildings_spec[typeShip]?.production_levels[1].build_cost.time ?? 0,
                  )}
                </Typography>
              </Box>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                px={1}
                mb={1}
              >
                <Typography
                  fontSize={"12px"}
                  color="primary.textResearchLab"
                  textAlign={"center"}
                  mt={1}
                >
                  Available
                </Typography>
                <Typography
                  fontSize={"12px"}
                  color="primary.textResearchLab"
                  textAlign={"center"}
                  mt={1}
                >
                  Loot Box
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mx={2} mb={2}>
            <Button
              onClick={handleClick}
              variant="blue"
              sx={{ width: "100%", height: "40px" }}
              disabled={
                (!!ship && ship.block_time_ready > Date.now()) ||
                !canPurchase("build/boat")
              }
            >
              {!ship
                ? "Rebuild the ship"
                : ship.block_time_ready > Date.now()
                  ? "Restored ..."
                  : "Send to the expedition"}
            </Button>
          </Box>
        </Dialog>
      </Suspense>
    </Fragment>
  );
};

export default ShipDialog;
